import { Table, TableBody, TableContainer } from '@mui/material';

import RefreshIcon from '../../../assets/svg/RefreshIcon';
import { AI_PROMPT_FEATURES } from '../../../constants';
import { useTablePagination, useTableSort } from '../../../hooks';
import { Button, Loader, Pagination, TableHead } from '../../Shared';
import { FEATURE_CELL_WIDTH } from './FeatureCell';
import { getFormattedClients } from './helper';
import { TableRow } from './TableRow';

const STATE = {
  HAS_DATA: 'hasData',
  HAS_NO_DATA: 'hasNoData',
  IS_DATA_LOADING: 'isDataLoading',
  IS_DATA_ERROR: 'isDataError',
};

const CELLS = [
  { id: 'tenantName', label: 'Client', width: '140px', sortable: true },
  ...AI_PROMPT_FEATURES.map(({ key, name }) => ({
    id: key,
    label: name,
    width: FEATURE_CELL_WIDTH,
    sortable: false,
  })),
  {
    id: 'contactsWithData',
    label: 'Leads with Aidentified data',
    width: '192px',
    sortable: true,
  },
  { id: 'lastUpdated', label: 'Last updated', width: '136px', sortable: true },
  { id: 'actions', label: '', width: '56px', sortable: false },
];

const getState = (data, isDataLoading, isDataError) => {
  if (isDataLoading) return STATE.IS_DATA_LOADING;
  if (isDataError) return STATE.IS_DATA_ERROR;
  if (data.length === 0) return STATE.HAS_NO_DATA;
  return STATE.HAS_DATA;
};

export const ClientsTable = ({
  clients,
  isClientsLoading,
  isClientsError,
  fetchClients,
  onOpenPrompt,
  onChangeStatus,
}) => {
  const { order, orderBy, onRequestSort, sortedRows } = useTableSort(
    'tenantName',
    getFormattedClients(clients),
  );
  const { page, count, rowsByPage, counter, setPage } =
    useTablePagination(sortedRows);

  const state = getState(sortedRows, isClientsLoading, isClientsError);

  return (
    <>
      {(state === STATE.HAS_DATA || state === STATE.HAS_NO_DATA) && (
        <>
          <TableContainer
            className={state === STATE.HAS_DATA ? 'h-full' : undefined}
          >
            <Table stickyHeader>
              <TableHead
                isCheckboxVisible={false}
                cells={CELLS}
                order={order}
                orderBy={orderBy}
                onRequestSort={onRequestSort}
              />
              {state === STATE.HAS_DATA && (
                <TableBody>
                  {rowsByPage.map((client, idx) => (
                    <TableRow
                      key={client.tenantId}
                      client={client}
                      withBorderBottom={rowsByPage.length !== idx + 1}
                      onOpenPrompt={onOpenPrompt}
                      onChangeStatus={onChangeStatus}
                    />
                  ))}
                </TableBody>
              )}
            </Table>
          </TableContainer>
          {state === STATE.HAS_DATA && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-base-800 text-[14px]/[20px]">
                {counter}
              </span>
              <Pagination count={count} page={page} onChange={setPage} />
            </div>
          )}
          {state === STATE.HAS_NO_DATA && (
            <div className="flex justify-center pt-2 px-6 pb-6">
              <span className="text-base-800 text-[16px]/[24px]">
                No clients match the filters
              </span>
            </div>
          )}
          <p className="text-base-800 text-[14px]/[20px]">
            Each client has its own prompts, written by ProspectIQ. Switching on
            a feature without a prompt opens the prompt window first. Precision
            Conversation off: callers get the current web-based customization.
            Precision Meeting off: the option is hidden.
          </p>
        </>
      )}
      {state === STATE.IS_DATA_LOADING && (
        <div className="h-full flex justify-center items-center">
          <Loader />
        </div>
      )}
      {state === STATE.IS_DATA_ERROR && (
        <div className="h-full flex justify-center items-center flex-col gap-3 bg-brand-300/[0.08] rounded-[20px]">
          <div className="flex justify-center items-center flex-col gap-1.5">
            <span className="text-base-1000 text-[22px]/[28px] font-semibold">
              Failed to load records
            </span>
            <span className="text-base-800 text-[16px]/[24px]">
              Error occurred while loading. Try again.
            </span>
          </div>
          <Button
            title="Reload"
            variant="text"
            className="text-destructive-500 !bg-transparent"
            iconStart={<RefreshIcon className="w-5 h-5 text-destructive-500" />}
            onClick={fetchClients}
          />
        </div>
      )}
    </>
  );
};
